// Copyright (c) 2025-2026 NacenCheck — TracePro Compliance-Grade Trace OS.
// Licensed under the MIT License — see LICENSE file in the repo root.
// SPDX-License-Identifier: MIT

"use client";

/**
 * SEC-R2-11 — nối phiên của AuthProvider tới mọi hàng đợi offline của host web.
 *
 *  1. Đăng ký nguồn chủ sở hữu cho `@tracepro/mobile-host` — adapter web/native
 *     (`platform.getSessionOwner()`) đọc từ đây; màn hình dùng chung đóng dấu và lọc
 *     hàng đợi nhật ký / HTX bằng giá trị này.
 *  2. Ghi phiên vào IndexedDB cho service worker (`lib/sw-queue-owner.ts`).
 *
 * Giá trị được cập nhật NGAY TRONG LÚC RENDER (gán biến, không gọi mạng) chứ không
 * trong effect: React chạy effect của con TRƯỚC cha, nên màn hình con phát lại hàng
 * đợi lúc mount sẽ đọc phải giá trị cũ nếu đợi effect của cầu nối này. Gắn cầu nối
 * làm con ĐẦU TIÊN của AuthProvider để nó render trước mọi trang.
 */

import { useEffect } from "react";
import { getAuthToken, useAuth } from "@tracepro/auth";
import { ACTIVE_USER_KEY, notifySessionOwnerChanged, setSessionOwnerSource, toOwner } from "@tracepro/mobile-host";
import type { SessionOwner } from "@tracepro/mobile-host";
import { writeSwActiveOwner } from "../lib/sw-queue-owner";
import { replaySwQueue } from "../lib/sw-queue-replay";
import { flushQueue as flushEventQueue } from "../lib/offline-sync";

/** Kết quả phát lại → cùng hình tin nhắn SW cũ; sw-registrar nghe và hiện toast. */
function notify(msg: Record<string, unknown>) {
  try {
    window.dispatchEvent(new CustomEvent("tp-offline-queue", { detail: msg }));
  } catch {
    /* SSR / không có window */
  }
}

/** Phát lại hàng đợi SW bằng token trong bộ nhớ của trang (lib/sw-queue-replay.ts). */
function replayNow() {
  void replaySwQueue({
    owner: current,
    getToken: getAuthToken,
    currentOwner: () => current,
    notify,
  }).catch(() => {
    /* IndexedDB hỏng — lượt sau thử lại */
  });
  // Hàng đợi sự kiện lib/offline-sync.ts (vận chuyển GPS trong vỏ app) — trước đây
  // không ai gọi flush; cùng luật chủ + token hiện tại.
  if (current) {
    void flushEventQueue(current, getAuthToken).catch(() => {
      /* lượt sau thử lại */
    });
  }
}

let current: SessionOwner | null = null;
setSessionOwnerSource(() => current);

/** Máy này chưa đăng xuất tường minh (logout xoá marker, phiên hết hạn thì không). */
function deviceUserStillSignedIn(): boolean {
  try {
    return !!window.localStorage.getItem(ACTIVE_USER_KEY);
  } catch {
    return false;
  }
}

export function OfflineOwnerBridge() {
  const { session, loading } = useAuth();
  const owner = session ? toOwner({ tenantId: session.user.tenantId, userId: session.user.id }) : null;
  current = owner;

  const key = loading ? "loading" : owner ? `${owner.tenantId}/${owner.userId}` : "";
  useEffect(() => {
    // Kho meta của SW (review H-1): KHÔNG xoá lúc AuthProvider còn đang khôi phục
    // phiên, và KHÔNG xoá khi phiên chỉ hết hạn / refresh hỏng vì mất mạng (marker
    // người dùng còn) — xoá lúc đó làm mọi POST SW xếp hàng thành dòng không chủ, kẹt
    // vĩnh viễn. Chỉ xoá khi đăng xuất tường minh (logout xoá marker) hoặc đổi người.
    if (loading) return;
    if (owner) {
      // H-2: phiên vừa có → phát lại dòng của đúng người này ngay (sau khi kho meta
      // đã mang đúng chủ — replay đòi meta trùng phiên).
      void writeSwActiveOwner(owner).then(replayNow);
    } else if (!deviceUserStillSignedIn()) {
      void writeSwActiveOwner(null);
    }
    // Màn hình đã mount trước khi phiên khôi phục xong (đọc được null) đọc lại.
    notifySessionOwnerChanged();
    // owner được suy ra từ key — chỉ ghi lại khi phiên thật sự đổi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // SW nhắn "có dòng chờ" (Background Sync / manual-drain) và lúc mạng về: trang
  // phát lại. SW không có token nên không tự gửi được.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onMsg = (e: MessageEvent) => {
      if ((e.data as { type?: string } | null)?.type === "offline-queue-replay") replayNow();
    };
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    sw?.addEventListener("message", onMsg);
    window.addEventListener("online", replayNow);
    return () => {
      sw?.removeEventListener("message", onMsg);
      window.removeEventListener("online", replayNow);
    };
  }, []);

  return null;
}
